// Pure, framework-free state for the live panel streams. Kept separate from
// the transport (stream.ts) so it can be unit-tested with plain objects.

export type StreamStatus = "idle" | "connecting" | "live" | "reconnecting" | "forbidden" | "stopped";

export interface FilterSample {
  nodeId: string;
  nodeName: string;
  filterName: string;
  filterKernelIndex: number;
  pps: number;
  bps: number;
  droppedRatelimit: number;
  droppedBandwidth: number;
  newSources: number;
}

export interface SnapshotIn {
  timestampMs: number;
  filters: FilterSample[];
}

export interface NodeBucket {
  pps: number;
  bps: number;
  droppedRatelimit: number;
  droppedBandwidth: number;
}

export interface SecondBucket {
  t: number; // unix seconds
  /** One entry per node that reported in this second (latest wins). */
  nodes: Map<string, NodeBucket>;
  /** Per node × filter, keyed filterKey(node, kernelIndex) (latest wins). */
  filters: Map<string, NodeBucket>;
}

export function filterKey(node: string, kernelIndex: number): string {
  return `${node}|${kernelIndex}`;
}

export interface LatestFilter extends FilterSample {
  receivedAtMs: number;
  history: number[]; // recent pps samples for sparklines, newest last
}

export type AttackState = "active" | "ended";

export interface Attack {
  id: string;
  state: AttackState;
  nodeId: string;
  nodeName: string;
  filterName: string;
  filterKernelIndex: number;
  /** undefined when only the END transition was ever received. */
  startedAtMs?: number;
  endedAtMs?: number;
  startPps?: number;
  endPps?: number;
  baselinePps: number;
  topSourceIps: string[];
}

export interface EventIn {
  attackId: string;
  start: boolean;
  nodeId: string;
  nodeName: string;
  filterName: string;
  filterKernelIndex: number;
  observedPps: number;
  baselinePps: number;
  topSourceIps: string[];
  timestampMs: number;
}

/** Master itself reports with node_id "" — give it a stable display key. */
export const MASTER_KEY = "__master__";

export function nodeKey(nodeId: string): string {
  return nodeId === "" ? MASTER_KEY : nodeId;
}

export function nodeLabel(nodeId: string, nodeName: string): string {
  if (nodeName) return nodeName;
  return nodeId === "" ? "Master" : nodeId;
}

const SPARK_POINTS = 60;

/**
 * A node's traffic for the overview chart. Every PAM-passed packet goes
 * through the permanent `default` filter (kernel index 0) and possibly one
 * named filter too, so summing filters would double-count; the default
 * filter's rate *is* the node's filtered traffic. Drops are distinct events
 * (a packet is dropped once) and are summed.
 */
export function nodeTotals(filters: FilterSample[]): NodeBucket {
  const def = filters.find((f) => f.filterKernelIndex === 0);
  let pps = def?.pps ?? 0;
  let bps = def?.bps ?? 0;
  if (!def) {
    for (const f of filters) {
      pps = Math.max(pps, f.pps);
      bps = Math.max(bps, f.bps);
    }
  }
  let droppedRatelimit = 0;
  let droppedBandwidth = 0;
  for (const f of filters) {
    droppedRatelimit += f.droppedRatelimit;
    droppedBandwidth += f.droppedBandwidth;
  }
  return { pps, bps, droppedRatelimit, droppedBandwidth };
}

export function bucketDrops(b: SecondBucket): { ratelimit: number; bandwidth: number } {
  let ratelimit = 0;
  let bandwidth = 0;
  for (const n of b.nodes.values()) {
    ratelimit += n.droppedRatelimit;
    bandwidth += n.droppedBandwidth;
  }
  return { ratelimit, bandwidth };
}

export class TrafficModel {
  readonly windowSec: number;
  buckets: SecondBucket[] = [];
  latest = new Map<string, LatestFilter>(); // key: nodeKey|kernelIndex
  /** Node keys in first-seen order — colors follow the node, never its rank. */
  nodeOrder: string[] = [];
  nodeNames = new Map<string, string>();
  /** Filter names by kernel index, and which filters each node has reported. */
  filterNames = new Map<number, string>();
  nodeFilters = new Map<string, Set<number>>();
  lastSnapshotMs = 0;

  constructor(windowSec: number) {
    this.windowSec = windowSec;
  }

  ingest(s: SnapshotIn, receivedAtMs: number): void {
    if (s.filters.length === 0) return;
    this.lastSnapshotMs = receivedAtMs;

    // Normally one snapshot = one node's filters (each node reports
    // separately); grouping keeps this correct if that ever changes.
    const byNode = new Map<string, FilterSample[]>();
    for (const f of s.filters) {
      const k = nodeKey(f.nodeId);
      if (!this.nodeNames.has(k)) this.nodeOrder.push(k);
      this.nodeNames.set(k, nodeLabel(f.nodeId, f.nodeName));
      const arr = byNode.get(k);
      if (arr) arr.push(f);
      else byNode.set(k, [f]);

      if (f.filterName) this.filterNames.set(f.filterKernelIndex, f.filterName);
      let seen = this.nodeFilters.get(k);
      if (!seen) this.nodeFilters.set(k, (seen = new Set()));
      seen.add(f.filterKernelIndex);

      const lk = filterKey(k, f.filterKernelIndex);
      const history = this.latest.get(lk)?.history ?? [];
      history.push(f.pps);
      if (history.length > SPARK_POINTS) history.splice(0, history.length - SPARK_POINTS);
      this.latest.set(lk, { ...f, receivedAtMs, history });
    }

    const bucket = this.bucketFor(Math.floor(s.timestampMs / 1000));
    if (!bucket) return;
    for (const [k, fs] of byNode) {
      bucket.nodes.set(k, nodeTotals(fs));
      for (const f of fs) {
        bucket.filters.set(filterKey(k, f.filterKernelIndex), {
          pps: f.pps,
          bps: f.bps,
          droppedRatelimit: f.droppedRatelimit,
          droppedBandwidth: f.droppedBandwidth,
        });
      }
    }
  }

  private bucketFor(t: number): SecondBucket | undefined {
    const newest = this.buckets[this.buckets.length - 1];
    if (newest && t <= newest.t - this.windowSec) return undefined; // outside the window
    const last = this.buckets[this.buckets.length - 1];
    if (last && last.t === t) return last;
    let b = this.buckets.find((x) => x.t === t);
    if (b) return b;
    b = { t, nodes: new Map(), filters: new Map() };
    this.buckets.push(b);
    if (last && t < last.t) this.buckets.sort((a, c) => a.t - c.t);
    const cutoff = this.buckets[this.buckets.length - 1]!.t - this.windowSec;
    let drop = 0;
    while (drop < this.buckets.length && this.buckets[drop]!.t <= cutoff) drop++;
    if (drop) this.buckets.splice(0, drop);
    return b;
  }
}

const MAX_ATTACKS = 500;

/**
 * DDoS attack history built from START/END transitions. The server's
 * event stream is replace-with-latest (ADR-0031), so a START can be
 * overwritten by its END before it is sent — an END for an unknown attack
 * still produces a (start-less) record rather than being dropped.
 */
export class AttackModel {
  attacks: Attack[] = []; // newest first

  constructor(initial: Attack[] = []) {
    this.attacks = initial.slice(0, MAX_ATTACKS);
  }

  ingest(e: EventIn): void {
    const idx = this.attacks.findIndex((a) => a.id === e.attackId);
    const existing = idx >= 0 ? this.attacks[idx] : undefined;
    if (e.start) {
      const a: Attack = {
        ...(existing ?? {}),
        id: e.attackId,
        state: existing?.state === "ended" ? "ended" : "active",
        nodeId: e.nodeId,
        nodeName: nodeLabel(e.nodeId, e.nodeName),
        filterName: e.filterName,
        filterKernelIndex: e.filterKernelIndex,
        startedAtMs: e.timestampMs,
        startPps: e.observedPps,
        baselinePps: e.baselinePps,
        topSourceIps: e.topSourceIps.length ? e.topSourceIps : (existing?.topSourceIps ?? []),
      };
      this.put(idx, a);
      return;
    }
    const a: Attack = {
      ...(existing ?? {
        id: e.attackId,
        nodeId: e.nodeId,
        nodeName: nodeLabel(e.nodeId, e.nodeName),
        filterName: e.filterName,
        filterKernelIndex: e.filterKernelIndex,
        baselinePps: e.baselinePps,
        topSourceIps: [],
      }),
      state: "ended",
      endedAtMs: e.timestampMs,
      endPps: e.observedPps,
    };
    if (e.topSourceIps.length) a.topSourceIps = e.topSourceIps;
    this.put(idx, a);
  }

  private put(idx: number, a: Attack): void {
    if (idx >= 0) this.attacks.splice(idx, 1);
    // Keep newest-first by the time the attack became known.
    this.attacks.unshift(a);
    this.attacks.sort((x, y) => sortKey(y) - sortKey(x));
    if (this.attacks.length > MAX_ATTACKS) this.attacks.length = MAX_ATTACKS;
  }

  active(): Attack[] {
    return this.attacks.filter((a) => a.state === "active");
  }

  dismiss(id: string): void {
    this.attacks = this.attacks.filter((a) => a.id !== id);
  }

  clearEnded(): void {
    this.attacks = this.attacks.filter((a) => a.state === "active");
  }
}

function sortKey(a: Attack): number {
  return a.startedAtMs ?? a.endedAtMs ?? 0;
}
